import React, { useEffect, useRef, useState, useCallback } from "react";
import { BASE_URL } from "../../../../Helpers";
import AxiosAuth from "../../../../services/requestApi";
import moment from "moment";
import LoadingGift from "../../../../assets/components-icons/LoadingGift";
import BoxSendMessage from "../box-send-message";
import "./send-message.sass"; // NO "./messenger-unified.sass"


const SendMessage = ({ user, refClickUser, userId, setUserHousing }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [housingId, setHousingId] = useState("");
  const [chatList, setChatList] = useState([]);
  const [toId, setToId] = useState("");
  const [page, setPage] = useState(1);
  const [disabledNewMessages, setDisabledNewMessages] = useState(false);
  
  // 🎯 NUEVOS ESTADOS para scroll mejorado
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  
  // 🎯 REFS mejoradas
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const getMessagesWith = async () => {
    setDisabledNewMessages(false);
    setLoading(true);
    try {
      let url;
      if (userId) {
        url = `${BASE_URL}/user/messages/with/${user.id}/${userId}`;
      } else {
        url = `${BASE_URL}/v2/user/messages/with/${user.id}?page=${page}&pageSize=10`;
      }

      const response = await AxiosAuth.get(url);
      if (response.data.length < 10) {
        setDisabledNewMessages(true);
      }

      const newMessages = response.data.length > 0 ? response.data.reverse() : [];

      setMessages((prevMessages) => [...prevMessages, ...newMessages]);
      setChatList(newMessages);
      setHousingId(newMessages[0]?.hosting || "");
      setUserHousing(newMessages[0]?.hosting || "");
      setToId(user.id);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  };

  useEffect(() => {
    if (user) {
      setMessages([]);
      setChatList([]);
      setPage(1);
    }
  }, [user]);

  useEffect(() => {
    if (user.id) {
      getMessagesWith();
    }
  }, [page, user.id]);

  useEffect(() => {
    if (Array.isArray(messages)) {
      setChatList(messages.sort((a, b) => a.id - b.id));
    }
  }, [messages]);

  // 🎯 FUNCIÓN DE SCROLL AUTOMÁTICO MEJORADA
  const scrollToBottom = useCallback((force = false) => {
    if (messagesEndRef.current && (shouldScrollToBottom || force)) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: "smooth",
        block: "end",
        inline: "nearest"
      });
    }
  }, [shouldScrollToBottom]);

  // 🎯 DETECTAR SCROLL MANUAL DEL USUARIO
  const handleScroll = useCallback((e) => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
    
    // Detectar si el usuario está haciendo scroll manual
    setIsUserScrolling(true);
    
    // Limpiar timeout previo
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Después de 1 segundo sin scroll, no es scroll manual
    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 1000);
    
    // Activar scroll automático solo si está cerca del final
    setShouldScrollToBottom(isNearBottom);
  }, []);

  // 🎯 SCROLL AUTOMÁTICO CUANDO LLEGAN MENSAJES NUEVOS
  useEffect(() => {
    if (chatList.length > 0 && !isUserScrolling) {
      // Pequeño delay para que se renderice el mensaje
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [chatList, scrollToBottom, isUserScrolling]);

  // 🎯 CARGAR MENSAJES ANTIGUOS SIN PERDER POSICIÓN
  const loadOlderMessages = async () => {
    if (loading || disabledNewMessages) return;
    
    const scrollHeight = messagesContainerRef.current?.scrollHeight || 0;
    const scrollTop = messagesContainerRef.current?.scrollTop || 0;
    
    // Desactivar scroll automático temporalmente
    setShouldScrollToBottom(false);
    setPage(prevPage => prevPage + 1);
    
    // Restaurar posición después de cargar mensajes
    setTimeout(() => {
      if (messagesContainerRef.current) {
        const newScrollHeight = messagesContainerRef.current.scrollHeight;
        const heightDifference = newScrollHeight - scrollHeight;
        messagesContainerRef.current.scrollTop = scrollTop + heightDifference;
      }
    }, 200);
  };

  // 🎯 FORZAR SCROLL AL FONDO AL ENVIAR MENSAJE
  const handleNewMessageSent = useCallback(() => {
    setShouldScrollToBottom(true);
    setTimeout(() => scrollToBottom(true), 100);
  }, [scrollToBottom]);

  return (
    <div className="sendMessage">
      {loading && (
        <div className="sendMessage__loading">
          <LoadingGift size={100} />
          <h2>Cargando mensajes...</h2>
        </div>
      )}

      <div 
        ref={messagesContainerRef}
        className="sendMessage__content"
        onScroll={handleScroll}
      >
        {!disabledNewMessages && !loading && (
          <button
            className="sendMessage__loadOlder"
            onClick={loadOlderMessages}
            disabled={loading}
            style={{
              width: '100%',
              padding: '10px',
              margin: '10px 0',
              background: '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            📜 Ver mensajes antiguos
          </button>
        )}
        
        {chatList.map((e, index) =>
          e.message.trim() !== "" ? (
            <div
              key={e.issued ? `${e.id}-${new Date(e.issued).getTime()}` : `message-${e.id}-${index}`} // ✅ CAMBIO: Usar ID + timestamp o índice
              className={
                e.from.id === user.id
                  ? "sendMessage__content__container"
                  : "sendMessage__content__containerLeft"
              }
            >
              <div
                className={
                  e.from.id === user.id
                    ? "sendMessage__content__container__message"
                    : "sendMessage__content__container__messageLeft"
                }
              >
                <p
                  className={
                    e.from.id === user.id
                      ? "sendMessage__content__container__message__textLeft"
                      : "sendMessage__content__container__message__textRight"
                  }
                >
                  {e.message}
                </p>
                <span className="sendMessage__content__container__message__date">
                  {moment(e.issued).utc(true).format("h:mm, D MMM ")}
                </span>
              </div>
            </div>
          ) : null
        )}
        
        {/* 🎯 MARCADOR PARA SCROLL AUTOMÁTICO */}
        <div ref={messagesEndRef} style={{ height: '1px', width: '100%' }} />
      </div>
      
      <BoxSendMessage
        setPage={setPage}
        setScollToDown={handleNewMessageSent}
        setChatList={setChatList}
        toId={toId}
        housingId={housingId}
        refClickUser={refClickUser}
      />
    </div>
  );
};

export default SendMessage;
